import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { dismissNotification } from '@/api/actions/notifications/notificationActions'
import { UI_PAGE_SIZE } from '@/api/actions/notifications/notificationConstants'
import { Mutations } from '@/api/mutations.enum'
import { Queries } from '@/api/queries.enum'
import type {
    DismissNotificationResponse,
    NotificationListResponse,
} from '@/api/actions/notifications/notificationActions.types'

interface UseDismissNotificationOptions {
    onSuccess?: (data: DismissNotificationResponse, notificationId: string) => void
    onError?: (error: AxiosError) => void
    onDismissStart?: (notificationId: string) => void
}

export const useDismissNotification = (options?: UseDismissNotificationOptions) => {
    const queryClient = useQueryClient()

    return useMutation<
        DismissNotificationResponse,
        AxiosError,
        string,
        { previousList?: NotificationListResponse; previousCount?: number }
    >({
        mutationKey: [Mutations.dismissNotification],
        // Wrap to match TanStack's expected mutation signature (variables only)
        mutationFn: (notificationId: string) => dismissNotification(notificationId),
        onMutate: async (notificationId) => {
            options?.onDismissStart?.(notificationId)

            await queryClient.cancelQueries({ queryKey: [Queries.getNotifications] })

            const previousList = queryClient.getQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE]) as
                | NotificationListResponse
                | undefined
            // Don't access count query data - it might trigger a refetch on first access
            // We don't actually need to store previousCount since we're not updating it optimistically
            const previousCount = undefined
            queryClient.setQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE], (old: NotificationListResponse) =>
                old
                    ? {
                          ...old,
                          results: (old.results ?? []).filter((n: { id: string }) => n.id !== notificationId),
                          count: Math.max(0, (old.count ?? 1) - 1),
                      }
                    : old,
            )

            // Don't update count optimistically - let polling handle count updates
            // This prevents extra API calls while optimistic updates handle the UI state
            // queryClient.setQueryData([Queries.getNotificationCount], (old: number = 0) => Math.max(0, old - 1))
            return { previousList, previousCount }
        },
        onSuccess: (data, notificationId) => {
            // No toast feedback for individual dismissals
            options?.onSuccess?.(data, notificationId)
        },
        onError: (error, notificationId, context) => {
            if (context?.previousList !== undefined) {
                queryClient.setQueryData([Queries.getNotifications, 1, UI_PAGE_SIZE], context.previousList)
            }
            // We're not storing previousCount anymore, so no need to restore it

            if (error.response?.status === 404 || error.response?.status === 409) {
                queryClient.invalidateQueries({ queryKey: [Queries.getNotifications] })
                queryClient.invalidateQueries({ queryKey: [Queries.getNotificationCount] })
                // No toast feedback for individual dismissals
            } else {
                // No toast error feedback for individual dismissals
                // Error is handled silently
            }

            options?.onError?.(error)
        },
        // No onSettled invalidation needed - optimistic updates handle success case
    })
}
